// src/components/navigation/StepCard.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogContent,
  Chip,
} from '@mui/material';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ZoomIn as ZoomInIcon,
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
} from '@mui/icons-material';

/**
 * Normalize step data from API (snake_case) to component format (camelCase)
 */
const normalizeStep = (step) => {
  if (!step) return null;

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  // Handle photo URL - prepend API base if it's a relative path
  let rawPhotoUrl = step.photo_url || step.photoUrl;
  // Transform /uploads/ to /api/uploads/ for correct API path
  if (rawPhotoUrl && rawPhotoUrl.startsWith('/uploads/')) {
    rawPhotoUrl = rawPhotoUrl.replace('/uploads/', '/api/uploads/');
  }
  const photoUrl = rawPhotoUrl
    ? (rawPhotoUrl.startsWith('http') ? rawPhotoUrl : `${API_BASE_URL}${rawPhotoUrl}`)
    : null;

  return {
    instruction: step.instruction,
    photoUrl,
    photoCaption: step.photo_caption || step.photoCaption,
    landmark: step.landmark || step.landmarkHint,
    landmarkHint: step.landmark || step.landmarkHint,
    distance: step.distance_meters ?? step.distance,
    estimatedTime: step.estimated_seconds ?? step.estimatedTime,
    floorChangeType: step.floor_change_type || step.floorChangeType,
    floorChange: step.floor_change_type && step.floor_change_type !== 'none',
    floorPlanId: step.floor_plan_id || step.floorPlanId,
    floorName: step.floor_name || step.floorName || `Floor`,
    nextFloorName: step.next_floor_name || step.nextFloorName,
    isAccessible: step.is_accessible ?? step.isAccessible,
    stepNumber: step.step_number || step.stepNumber,
  };
};

/**
 * Step-by-step navigation instruction card
 */
const StepCard = ({
  step: rawStep,
  stepNumber,
  totalSteps,
  onNext,
  onPrevious,
  showGpsWarning = false,
  isLastStep = false,
}) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Normalize the step data from API format
  const step = normalizeStep(rawStep);

  if (!step) {
    return null;
  }

  const hasFloorChange = step.floorChange;

  return (
    <>
      <Card
        elevation={4}
        sx={{
          width: '100%',
          maxWidth: 600,
        }}
      >
        {/* GPS Warning Banner */}
        {showGpsWarning && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            GPS signal weak. Following step-by-step instructions.
          </Alert>
        )}

        {/* Floor Change Alert */}
        {hasFloorChange && (
          <Alert
            severity="info"
            icon={step.floorChangeType === 'stairs' ? <StairsIcon /> : <ElevatorIcon />}
            sx={{ borderRadius: 0 }}
          >
            Take {step.floorChangeType === 'stairs' ? 'stairs' : 'elevator'} to{' '}
            {step.nextFloorName}
          </Alert>
        )}

        {/* Step Photo */}
        {step.photoUrl && (
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              image={step.photoUrl}
              alt={step.instruction}
              sx={{
                cursor: 'pointer',
                maxHeight: 300,
                objectFit: 'contain',
                objectPosition: 'center',
                bgcolor: 'grey.100',
              }}
              onClick={() => setImageDialogOpen(true)}
            />
            <IconButton
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
              onClick={() => setImageDialogOpen(true)}
            >
              <ZoomInIcon />
            </IconButton>
          </Box>
        )}

        <CardContent>
          {/* Step Number and Floor */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Step {stepNumber} of {totalSteps}
            </Typography>
            <Chip label={step.floorName} size="small" variant="outlined" />
          </Box>

          {/* Main Instruction - Prominent */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              lineHeight: 1.3,
            }}
          >
            {step.instruction}
          </Typography>

          {/* Distance and Time - Inline with instruction context */}
          {(step.distance || step.estimatedTime) && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                py: 1,
                px: 1.5,
                backgroundColor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {step.distance > 0 && (
                <Typography variant="body2" color="text.secondary">
                  📏 {step.distance}m
                </Typography>
              )}
              {step.estimatedTime > 0 && (
                <Typography variant="body2" color="text.secondary">
                  ⏱️ ~{step.estimatedTime}s
                </Typography>
              )}
            </Box>
          )}

          {/* Landmark Hint - Secondary info */}
          {step.landmarkHint && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                py: 0.5,
                px: 1,
                backgroundColor: 'info.lighter',
                borderLeft: '3px solid',
                borderColor: 'info.main',
                borderRadius: '0 4px 4px 0',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Look for: {step.landmarkHint}
            </Typography>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={onPrevious}
              disabled={stepNumber === 1}
            >
              Back
            </Button>

            <Button
              variant="contained"
              endIcon={<NavigateNextIcon />}
              onClick={onNext}
            >
              {isLastStep ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Image Zoom Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={step.photoUrl}
            alt={step.instruction}
            style={{ width: '100%', display: 'block' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StepCard;
